// /src/utils/ranking.js
import { normalizeScore } from './calibration';

function toSpace(displayMode) {
  return displayMode === 'Calibrated' ? 'cal'
       : displayMode === 'Raw'        ? 'raw'
       : /* Auto */                     'auto';
}

// Distance to upper-right (smaller is better)
function quickWinsKey(u, ctx, calMap) {
  const space = toSpace(ctx.displayMode);
  let v, e;
  
  if (space === 'cal' && calMap && calMap[u.use_case_id]) {
    v = calMap[u.use_case_id].value;
  } else if (u.value_score_cal !== undefined) {
    v = u.value_score_cal;
  } else {
    v = u.value_score;
  }
  
  if (space === 'cal' && calMap && calMap[u.use_case_id]) {
    e = calMap[u.use_case_id].eoi;
  } else if (u.implementation_score_cal !== undefined) {
    e = u.implementation_score_cal;
  } else {
    e = u.implementation_score;
  }
  
  const nv = normalizeScore(v, space, ctx.valueBounds);
  const ne = normalizeScore(e, space, ctx.eoiBounds);
  const dist = Math.hypot(1 - nv, 1 - ne); // 0 = best
  return dist;
}

function totalKey(u, ctx, calMap, weights) {
  const space = toSpace(ctx.displayMode);
  let v, e;
  
  if (space === 'cal' && calMap && calMap[u.use_case_id]) {
    v = calMap[u.use_case_id].value;
  } else if (u.value_score_cal !== undefined) {
    v = u.value_score_cal;
  } else {
    v = u.value_score;
  }
  
  if (space === 'cal' && calMap && calMap[u.use_case_id]) {
    e = calMap[u.use_case_id].eoi;
  } else if (u.implementation_score_cal !== undefined) {
    e = u.implementation_score_cal;
  } else {
    e = u.implementation_score;
  }
  
  const nv = normalizeScore(v, space, ctx.valueBounds);
  const ne = normalizeScore(e, space, ctx.eoiBounds);
  // weights on normalized scale assure lens coherence w/ axes
  return -(weights.v * nv + weights.e * ne); // negative for descending
}

export function sortUseCases(items, ctx, calMap) {
  console.log('[sortUseCases] INPUT - Length:', items.length, 'First 3:', 
    items.slice(0, 3).map(u => `${u.title || u.name} (rank: ${u.rank})`)
  );
  
  if (!Array.isArray(items) || !items.length) return items || [];
  
  // CRITICAL: Database ranks are REQUIRED - no frontend fallback calculation
  // Check which items are missing valid ranks
  const itemsWithoutRanks = items.filter(u => u.rank == null || u.rank <= 0 || u.rank >= 9999);
  
  console.log('[sortUseCases] Rank validation:', {
    total: items.length,
    withoutValidRanks: itemsWithoutRanks.length,
    firstFewWithoutRanks: itemsWithoutRanks.slice(0, 5).map(u => ({
      title: u.title || u.name,
      rank: u.rank,
      rankType: typeof u.rank
    }))
  });
  
  // If ANY items are missing ranks, throw an error - do NOT fall back to calculations
  if (itemsWithoutRanks.length > 0) {
    const errorMessage = `DATABASE ERROR: ${itemsWithoutRanks.length} of ${items.length} use cases are missing valid ranks. ` +
      `Missing ranks for: ${itemsWithoutRanks.map(u => u.title || u.name).slice(0, 10).join(', ')}` +
      (itemsWithoutRanks.length > 10 ? ` and ${itemsWithoutRanks.length - 10} more` : '') + '. ' +
      `Fix the client_use_case_scores table in the database by ensuring all use cases have valid rank values.`;
    
    console.error('[sortUseCases] CRITICAL ERROR:', errorMessage, {
      missingRankUseCases: itemsWithoutRanks.map(u => ({
        use_case_id: u.use_case_id,
        title: u.title || u.name,
        rank: u.rank
      }))
    });
    
    throw new Error(errorMessage);
  }
  
  // All items have valid ranks - sort by them exclusively
  console.log('[sortUseCases] All items have valid database ranks - sorting by rank');
  
  const sorted = [...items].sort((a, b) => {
    // Primary sort: by rank (ascending)
    if (a.rank !== b.rank) return a.rank - b.rank;
    
    // Tie-breaker: alphabetical by title
    const titleA = (a.title || a.name || '').toLowerCase();
    const titleB = (b.title || b.name || '').toLowerCase();
    return titleA.localeCompare(titleB);
  });
  
  console.log('[sortUseCases] OUTPUT (using DB ranks) - First 3:', 
    sorted.slice(0, 3).map(u => `${u.title || u.name} (rank: ${u.rank})`)
  );
  
  return sorted;
}